// MCHD Room Scheduler - Error Boundary Component
// Handles authentication and application errors gracefully

import { Component, ReactNode, ErrorInfo } from 'react';
import { Box, Container, Stack, Text, Group, Button } from '@mantine/core';
import { IconAlertCircle, IconHome } from '@tabler/icons-react';
import DynamicRefreshButton from './atoms/DynamicRefreshButton';

/**
 * Props for the ErrorBoundary component
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * State for the ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary for handling authentication and application errors
 * 
 * @param children - Child components to wrap
 * @param fallback - Custom fallback UI
 * @param onError - Error callback function
 * @returns JSX element
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error occurs
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Handle error and log it
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    });

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call error callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error state
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Reload the page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  /**
   * Go to home page
   */
  handleGoHome = (): void => {
    window.location.href = '/';
  };

  /**
   * Render error UI or children
   */
  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Container size="sm">
            <Stack ta="center" gap="xl">
              {/* Error Icon */}
              <Box
                style={{
                  width: 80,
                  height: 80,
                  backgroundColor: '#dc2626',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(220, 38, 38, 0.3)',
                }}
              >
                <IconAlertCircle size={40} color="white" />
              </Box>

              {/* Error Content */}
              <Stack ta="center" gap="lg">
                <Text
                  size="xl"
                  fw={700}
                  color="#dc2626"
                  ta="center"
                  style={{ fontFamily: 'Rubik, sans-serif' }}
                >
                  {this.state.error ? getErrorType(this.state.error) === 'auth' ? 'Authentication Error' : 
                   getErrorType(this.state.error) === 'data' ? 'Data Error' :
                   getErrorType(this.state.error) === 'network' ? 'Network Error' : 'Something went wrong' : 'Something went wrong'}
                </Text>
                
                <Text
                  size="lg"
                  color="#7f1d1d"
                  ta="center"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {this.state.error ? getErrorType(this.state.error) === 'auth' ? 'There was a problem with your authentication. Please log in again.' :
                   getErrorType(this.state.error) === 'data' ? 'There was a problem loading the data. Please refresh the page or try again later.' :
                   getErrorType(this.state.error) === 'network' ? 'Unable to connect to the server. Please check your internet connection and try again.' :
                   'We encountered an unexpected error. Please try again.' : 'We encountered an unexpected error. Please try again.'}
                </Text>

                {/* Error Details (Development Only) */}
                {process.env['NODE_ENV'] === 'development' && this.state.error && (
                  <Box
                    style={{
                      padding: '16px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      width: '100%',
                    }}
                  >
                    <Text size="sm" color="#991b1b" fw={600} mb="xs">
                      Error Details:
                    </Text>
                    <Text size="xs" color="#991b1b" style={{ fontFamily: 'monospace' }}>
                      {this.state.error.message}
                    </Text>
                    {this.state.errorInfo && (
                      <Text size="xs" color="#991b1b" style={{ fontFamily: 'monospace' }} mt="xs">
                        {this.state.errorInfo.componentStack}
                      </Text>
                    )}
                  </Box>
                )}

                {/* Action Buttons */}
                <Group gap="md" mt="lg">
                  <DynamicRefreshButton
                    onRefresh={this.state.error && getErrorType(this.state.error) === 'auth' ? () => window.location.href = '/login' : this.handleReset}
                    variant="filled"
                    color={this.state.error && getErrorType(this.state.error) === 'auth' ? 'red' : 'blue'}
                    size="md"
                    children={this.state.error && getErrorType(this.state.error) === 'auth' ? 'Go to Login' : 'Try Again'}
                  />
                  
                  <DynamicRefreshButton
                    onRefresh={this.handleReload}
                    variant="outline"
                    color="gray"
                    size="md"
                    children="Reload Page"
                  />
                  
                  <Button
                    leftSection={<IconHome size={16} />}
                    onClick={this.handleGoHome}
                    variant="outline"
                    color="gray"
                    size="md"
                  >
                    Go Home
                  </Button>
                </Group>
              </Stack>
            </Stack>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Error type detection utility
 */
function getErrorType(error: Error): 'auth' | 'data' | 'network' | 'component' | 'unknown' {
  const message = error.message.toLowerCase();
  
  if (message.includes('unauthorized') || message.includes('token') || message.includes('auth')) {
    return 'auth';
  }
  
  if (message.includes('fetch') || message.includes('network') || message.includes('connection')) {
    return 'network';
  }
  
  if (message.includes('map') || message.includes('undefined') || message.includes('null')) {
    return 'data';
  }
  
  if (message.includes('component') || message.includes('render')) {
    return 'component';
  }
  
  return 'unknown';
}

/**
 * Enhanced error boundary with error type detection
 */
export function EnhancedErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ErrorBoundary
      fallback={undefined}
      onError={(error, errorInfo) => {
        console.error('EnhancedErrorBoundary caught an error:', error, errorInfo);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Authentication-specific error boundary
 */
export function AuthErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ErrorBoundary
      fallback={
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Container size="sm">
            <Stack ta="center" gap="xl">
              <Box
                style={{
                  width: 80,
                  height: 80,
                  backgroundColor: '#dc2626',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(220, 38, 38, 0.3)',
                }}
              >
                <IconAlertCircle size={40} color="white" />
              </Box>

              <Stack ta="center" gap="lg">
                <Text
                  size="xl"
                  fw={700}
                  color="#dc2626"
                  ta="center"
                  style={{ fontFamily: 'Rubik, sans-serif' }}
                >
                  Authentication Error
                </Text>
                
                <Text
                  size="lg"
                  color="#7f1d1d"
                  ta="center"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  There was a problem with your authentication. Please log in again.
                </Text>

                <DynamicRefreshButton
                  onRefresh={() => window.location.reload()}
                  variant="filled"
                  color="blue"
                  size="lg"
                  children="Reload and Login"
                />
              </Stack>
            </Stack>
          </Container>
        </Box>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
